// PersonalizationProvider.tsx - Wraps app with personalization features
// Handles session tracking, achievements, delight moments, and global overlays

import { useEffect, useState, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  startSession,
  endSession,
  loadProfile,
  trackFeatureUsage,
  UserProfile,
} from '../services/personalizationService';
import { ReturnUserBriefing } from './ReturnUserBriefing';
import { MarketOpenCelebration, StreakDisplay } from './ui/DelightMoments';
import { ContextualHelpButton } from './help/ContextualHelp';
import { AchievementToastContainer, useAchievementManager } from './ui/AchievementSystem';
import './PersonalizationProvider.css';

// Context for personalization data
interface PersonalizationContextType {
  profile: UserProfile;
  refreshProfile: () => void;
  checkAchievement: (id: string) => boolean;
}

const PersonalizationContext = createContext<PersonalizationContextType | null>(null);

export function usePersonalization() {
  const ctx = useContext(PersonalizationContext);
  if (!ctx) {
    throw new Error('usePersonalization must be used within PersonalizationProvider');
  }
  return ctx;
}

interface PersonalizationProviderProps {
  children: React.ReactNode;
}

export function PersonalizationProvider({ children }: PersonalizationProviderProps) {
  const [profile, setProfile] = useState<UserProfile>(() => loadProfile());
  const [showBriefing, setShowBriefing] = useState(false);
  const location = useLocation();
  const { toast, dismissToast, checkAndAward } = useAchievementManager();

  // Start session on mount
  useEffect(() => {
    const updatedProfile = startSession();
    setProfile(updatedProfile);

    // Check if should show return briefing
    // Delay slightly to let the app render
    const briefingTimer = setTimeout(() => {
      const p = loadProfile();
      // Show briefing if more than 8 hours since last session and not first session
      const lastSession = p.last_session_date ? new Date(p.last_session_date) : null;
      const hoursSince = lastSession
        ? (Date.now() - lastSession.getTime()) / (1000 * 60 * 60)
        : 0;

      if (hoursSince >= 8 && p.session_count > 1 && !sessionStorage.getItem('return_briefing_shown')) {
        setShowBriefing(true);
      }
    }, 1500);

    // End session on unmount or page close
    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(briefingTimer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession();
    };
  }, []);

  // Track feature usage on route change
  useEffect(() => {
    const featureMap: Record<string, string> = {
      '/': 'dashboard',
      '/watchlist': 'watchlist',
      '/portfolio': 'portfolio',
      '/screener': 'screener',
      '/alerts': 'alerts',
      '/backtest': 'backtest',
      '/strategy-studio': 'strategy_studio',
      '/macro': 'macro',
      '/earnings': 'earnings',
      '/news': 'news',
      '/options': 'options',
      '/charts': 'charts',
      '/ai': 'ai_analysis',
      '/dividends': 'dividends',
    };

    const feature = featureMap[location.pathname];
    if (feature) {
      trackFeatureUsage(feature);
    }

    // Special handling for stock detail pages
    if (location.pathname.startsWith('/stock/')) {
      trackFeatureUsage('stock_detail');
    }
  }, [location.pathname]);

  const refreshProfile = () => {
    setProfile(loadProfile());
  };

  const checkAchievement = (id: string): boolean => {
    const earned = checkAndAward(id);
    if (earned) {
      refreshProfile();
    }
    return earned;
  };

  const value: PersonalizationContextType = {
    profile,
    refreshProfile,
    checkAchievement,
  };

  return (
    <PersonalizationContext.Provider value={value}>
      {children}

      {/* Global overlays and components */}

      {showBriefing && (
        <ReturnUserBriefing onDismiss={() => setShowBriefing(false)} />
      )}

      <MarketOpenCelebration />

      <ContextualHelpButton />

      <AchievementToastContainer toast={toast} onDismiss={dismissToast} />

      {/* Streak display in corner - only show if streak > 2 */}
      {profile.streak_days >= 3 && (
        <div className="streak-corner-display">
          <StreakDisplay compact />
        </div>
      )}
    </PersonalizationContext.Provider>
  );
}

export default PersonalizationProvider;
